import * as React from "react";
import { Button } from "./ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "./ui/card";
import { Github, Gitlab } from "lucide-react";
import { UserContext } from "@/context/user";

const providerIcons: { [key: string]: React.ElementType } = {
  apple: () => <>{null}</>,
  bitbucket: () => <>{null}</>,
  github: Github,
  gitlab: Gitlab,
  google: () => <>{null}</>,
};

export const LoginForm = () => {
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [providers, setProviders] = React.useState<string[]>([]);
  const { setLoginProvider } = React.useContext(UserContext);

  React.useEffect(() => {
    const fetchProviders = async () => {
      try {
        const response = await fetch(
          `${import.meta.env.VITE_PUBLIC_API_ENDPOINT}/auth/providers`,
        );
        if (response.ok) {
          const data = await response.json();
          setProviders(data.providers);
        } else {
          console.error("Failed to fetch providers", response.statusText);
        }
      } catch (error) {
        console.error("Error fetching providers:", error);
      }
    };
    fetchProviders();
  }, []);

  const handleLogin = (providerName: string) => {
    setIsLoading(true);
    setLoginProvider(providerName);

    window.location.href = `${
      import.meta.env.VITE_PUBLIC_API_ENDPOINT
    }/auth/${providerName}/authorize`;
  };

  return (
    <div className="lg:p-8">
      <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]">
        <Card
          className="animate-[shadow-move_20s_ease-in-out_infinite] shadow-[20px_20px_60px_0px_rgba(0,0,0,1)] transition-shadow"
          style={{
            // fallback for browsers not supporting tailwind's arbitrary values
            boxShadow: "20px 20px 60px 0px rgba(0,0,0,0.25)",
          }}
        >
          <CardHeader className="space-y-1">
            <h1 className="text-center text-2xl font-semibold tracking-tight">Sign in</h1>
            <CardDescription className="text-center">
              Continue with your account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4">
              {providers.map((providerName) => {
                const Icon =
                  providerIcons[providerName.toLowerCase()] || Github;
                return (
                  <Button
                    key={providerName}
                    variant="outline"
                    type="button"
                    disabled={isLoading}
                    className="w-full"
                    onClick={() => handleLogin(providerName)}
                  >
                    <Icon className="mr-2 h-4 w-4" />
                    Continue with{" "}
                    {providerName.charAt(0).toUpperCase() +
                      providerName.slice(1)}
                  </Button>
                );
              })}
            </div>
          </CardContent>
        </Card>
        <p className="px-8 text-center text-xs text-muted-foreground">
          By signing in, you agree to comply with our<br/>
          <a
            href="https://buzooka.in/legal"
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-4 hover:text-primary"
          >
            legal terms
          </a>
          .
        </p>
        <style>
          {`
              @keyframes shadow-move {
                0% {
                  box-shadow: -600px 1000px 400px 200px rgba(162,244,253,1);
                }
                10% {
                  box-shadow: 500px -1000px 400px 200px rgba(252,206,232,1);
                }
                100% {
                  box-shadow: 500px -1000px 400px 200px rgba(252,206,232,1);
                }
              }
            `}
        </style>
      </div>
    </div>
  );
};
